import React from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Bed, BedType, Bedroom } from '../types/bedroom';
import { SingleBedIcon, DoubleBedIcon } from './BedIcons';

interface BedroomEditorProps {
  bedrooms: Bedroom[];
  onChange: (bedrooms: Bedroom[]) => void;
  disabled?: boolean;
}

const BED_DIMENSIONS: Record<BedType, string[]> = {
  single: ['80x190', '80x200', '90x190', '90x200'],
  double: ['140x190', '140x200', '160x200', '180x200'],
};

export const BedroomEditor: React.FC<BedroomEditorProps> = ({ bedrooms, onChange, disabled }) => {
  const { t } = useTranslation();

  const addBedroom = () => {
    onChange([...bedrooms, { name: '', beds: [{ type: 'double' }] }]);
  };

  const removeBedroom = (index: number) => {
    onChange(bedrooms.filter((_, i) => i !== index));
  };

  const updateBedroomName = (index: number, name: string) => {
    const updated = [...bedrooms];
    updated[index] = { ...updated[index], name };
    onChange(updated);
  };

  const addBed = (bedroomIndex: number) => {
    const updated = [...bedrooms];
    updated[bedroomIndex] = {
      ...updated[bedroomIndex],
      beds: [...updated[bedroomIndex].beds, { type: 'single' }],
    };
    onChange(updated);
  };

  const removeBed = (bedroomIndex: number, bedIndex: number) => {
    const updated = [...bedrooms];
    updated[bedroomIndex] = {
      ...updated[bedroomIndex],
      beds: updated[bedroomIndex].beds.filter((_, i) => i !== bedIndex),
    };
    onChange(updated);
  };

  const updateBed = (bedroomIndex: number, bedIndex: number, updates: Partial<Bed>) => {
    const updated = [...bedrooms];
    const bed = { ...updated[bedroomIndex].beds[bedIndex], ...updates };
    if (updates.type && updates.type !== updated[bedroomIndex].beds[bedIndex].type) {
      bed.dimensions = undefined;
    }
    updated[bedroomIndex] = {
      ...updated[bedroomIndex],
      beds: updated[bedroomIndex].beds.map((b, i) => (i === bedIndex ? bed : b)),
    };
    onChange(updated);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{t('bedrooms.title')}</Typography>

      {bedrooms.map((bedroom, bedroomIndex) => (
        <Paper key={bedroomIndex} variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <IconButton
            size="small"
            onClick={() => removeBedroom(bedroomIndex)}
            disabled={disabled}
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            <Close fontSize="small" />
          </IconButton>

          <Stack spacing={1.5}>
            <TextField
              size="small"
              label={t('bedrooms.bedroomName')}
              placeholder={t('bedrooms.bedroomPlaceholder', { number: bedroomIndex + 1 })}
              value={bedroom.name}
              onChange={(e) => updateBedroomName(bedroomIndex, e.target.value)}
              disabled={disabled}
              fullWidth
            />

            {bedroom.beds.map((bed, bedIndex) => (
              <Stack key={bedIndex} direction="row" spacing={1} alignItems="center">
                <Box sx={{ color: bed.type === 'double' ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                  {bed.type === 'double' ? <DoubleBedIcon /> : <SingleBedIcon />}
                </Box>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t('bedrooms.bedType')}</InputLabel>
                  <Select
                    value={bed.type}
                    label={t('bedrooms.bedType')}
                    onChange={(e) => updateBed(bedroomIndex, bedIndex, { type: e.target.value as BedType })}
                    disabled={disabled}
                  >
                    <MenuItem value="single">{t('bedrooms.single')}</MenuItem>
                    <MenuItem value="double">{t('bedrooms.double')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t('bedrooms.dimensions')}</InputLabel>
                  <Select
                    value={bed.dimensions || ''}
                    label={t('bedrooms.dimensions')}
                    onChange={(e) => updateBed(bedroomIndex, bedIndex, { dimensions: e.target.value || undefined })}
                    disabled={disabled}
                  >
                    <MenuItem value="">&mdash;</MenuItem>
                    {BED_DIMENSIONS[bed.type].map((dim) => (
                      <MenuItem key={dim} value={dim}>{dim} cm</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {bedroom.beds.length > 1 && (
                  <IconButton size="small" onClick={() => removeBed(bedroomIndex, bedIndex)} disabled={disabled}>
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}

            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => addBed(bedroomIndex)}
              disabled={disabled}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('bedrooms.addBed')}
            </Button>
          </Stack>
        </Paper>
      ))}

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={addBedroom}
        disabled={disabled}
        fullWidth
      >
        {t('bedrooms.addBedroom')}
      </Button>
    </Stack>
  );
};
